import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
    doc,
    onSnapshot,
    setDoc,
    updateDoc,
    arrayUnion,
    arrayRemove,
    getDoc
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

export const useRoster = () => {
    const { currentUser: user } = useAuth();
    const [roster, setRoster] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user) {
            setRoster([]);
            setLoading(false);
            return;
        }

        const rosterRef = doc(db, 'rosters', user.uid);

        const unsubscribe = onSnapshot(rosterRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                // Ensure we have an array of players
                setRoster(data.players || []);
            } else {
                setRoster([]);
            }
            setLoading(false);
        }, (err) => {
            console.error("Error fetching roster:", err);
            setError(err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user]);

    const addPlayer = useCallback(async (playerData) => {
        if (!user) return;
        const rosterRef = doc(db, 'rosters', user.uid);

        const newPlayer = {
            id: 'player_' + Date.now(),
            name: '',
            number: '',
            position: '',
            iconColor: '#3b82f6', // Default blue
            overallRank: 5,
            attributes: {
                flagPulling: 5,
                defensiveRushing: 5,
                defensiveCoverage: 5,
                catching: 5,
                agility: 5,
                speed: 5,
                throwing: 5
            },
            roles: [], // ['QB', 'WR', 'CB', etc]
            ...playerData,
            createdAt: new Date().toISOString()
        };

        try {
            const docSnap = await getDoc(rosterRef);
            if (!docSnap.exists()) {
                await setDoc(rosterRef, { players: [newPlayer] });
            } else {
                await updateDoc(rosterRef, {
                    players: arrayUnion(newPlayer)
                });
            }
            return newPlayer;
        } catch (err) {
            console.error("Error adding player:", err);
            throw err;
        }
    }, [user]);

    const updatePlayer = useCallback(async (playerId, updates) => {
        if (!user) return;
        const rosterRef = doc(db, 'rosters', user.uid);

        try {
            const docSnap = await getDoc(rosterRef);
            if (docSnap.exists()) {
                const currentPlayers = docSnap.data().players || [];
                const updatedPlayers = currentPlayers.map(p =>
                    p.id === playerId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
                );
                await updateDoc(rosterRef, { players: updatedPlayers });
            }
        } catch (err) {
            console.error("Error updating player:", err);
            throw err;
        }
    }, [user]);

    const removePlayer = useCallback(async (playerId) => {
        if (!user) return;
        const rosterRef = doc(db, 'rosters', user.uid);

        try {
            const docSnap = await getDoc(rosterRef);
            if (docSnap.exists()) {
                const currentPlayers = docSnap.data().players || [];
                const updatedPlayers = currentPlayers.filter(p => p.id !== playerId);
                await updateDoc(rosterRef, { players: updatedPlayers });
            }
        } catch (err) {
            console.error("Error removing player:", err);
            throw err;
        }
    }, [user]);

    return {
        roster,
        loading,
        error,
        addPlayer,
        updatePlayer,
        removePlayer
    };
};
